/**
 * FormField molecule - combines label, input, and error handling
 * Provides consistent form field layout across the application
 */

import {
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
} from '@chakra-ui/react';
import React from 'react';
import { BaseComponentProps } from '../../types';

export interface FormFieldProps extends BaseComponentProps {
  /** Field label */
  label?: string;
  /** Field name (for form handling) */
  name?: string;
  /** Field type (for input elements) */
  type?: string;
  /** Field placeholder */
  placeholder?: string;
  /** Field value */
  value?: any;
  /** Change handler */
  onChange?: (value: any) => void;
  /** Field icon */
  icon?: React.ReactElement;
  /** Whether field is required */
  required?: boolean;
  /** Error message */
  error?: string;
  /** Helper text */
  helperText?: string;
  /** Help text (alias for helperText) */
  help?: string;
  /** Input or form control element */
  children?: React.ReactElement;
  /** Whether to show required asterisk */
  showRequiredIndicator?: boolean;
  /** Whether field is disabled */
  disabled?: boolean;
  /** Flex basis for responsive layouts */
  flex?: number;
}

export const FormField: React.FC<FormFieldProps> = ({
  label,
  name,
  type = 'text',
  placeholder,
  value,
  onChange,
  icon,
  required = false,
  error,
  helperText,
  help,
  children,
  showRequiredIndicator = true,
  disabled = false,
  flex,
  className,
  testId,
}) => {
  const isInvalid = Boolean(error);

  return (
    <FormControl
      isRequired={required}
      isInvalid={isInvalid}
      isDisabled={disabled}
      className={className}
      data-testid={testId}
      flex={flex}
    >
      {label && <FormLabel mb={2}>{label}</FormLabel>}

      {children || (
        <div
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <input
            type={type}
            name={name}
            placeholder={placeholder}
            value={value || ''}
            onChange={(e) => onChange?.(e.target.value)}
            disabled={disabled}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #E2E8F0',
              borderRadius: '8px',
              fontSize: '16px',
              lineHeight: '24px',
              minHeight: '48px',
              backgroundColor: disabled ? '#F7FAFC' : 'white',
              paddingLeft: icon ? '48px' : '16px',
              paddingRight: '16px',
              outline: 'none',
              transition: 'border-color 0.2s, box-shadow 0.2s',
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#3182ce';
              e.target.style.boxShadow = '0 0 0 1px #3182ce';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#E2E8F0';
              e.target.style.boxShadow = 'none';
            }}
          />
          {icon && (
            <div
              style={{
                position: 'absolute',
                left: '16px',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#A0AEC0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '20px',
                height: '20px',
                pointerEvents: 'none',
                zIndex: 1,
              }}
            >
              {icon}
            </div>
          )}
        </div>
      )}

      {error ? (
        <FormErrorMessage mt={2}>{error}</FormErrorMessage>
      ) : (
        (helperText || help) && (
          <FormHelperText mt={2}>{helperText || help}</FormHelperText>
        )
      )}
    </FormControl>
  );
};
